import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Trash2, Bot, User, ExternalLink, CheckCircle, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

const SESSION_ID = "main";

// Suggested quick actions
const SUGGESTIONS = [
  "¿Cuántos pedidos tengo pendientes de surtir?",
  "¿Qué productos tienen stock bajo?",
  "¿Cuáles son mis productos más vendidos este mes?",
  "¿Cuánto vendí este mes?",
  "Crea un pedido para el cliente Sara con 2kg del primer producto disponible",
];

function ToolBadge({ tool }) {
  const labels = {
    get_customers: "Buscando clientes",
    get_products: "Consultando productos",
    create_sale: "Creando documento",
    get_pending_fulfillment: "Consultando surtido",
    get_low_stock: "Revisando stock",
    get_customer_balance: "Consultando saldo",
    get_price_lists: "Consultando precios",
    get_sales_summary: "Calculando ventas",
    get_top_products: "Analizando productos top",
    update_sale_status: "Actualizando estado",
    compose_whatsapp_message: "Preparando WhatsApp",
  };
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-brand-50 text-brand-700 border border-brand-100 px-2 py-0.5 rounded-full">
      {labels[tool] || `⚙️ ${tool}`}
    </span>
  );
}

function WhatsAppButton({ data }) {
  const handleOpen = () => {
    const phone = data.phone?.replace(/\D/g, "") || "";
    const text = encodeURIComponent(data.whatsapp_message || "");
    const url = phone
      ? `https://web.whatsapp.com/send?phone=${phone}&text=${text}`
      : `https://web.whatsapp.com/send?text=${text}`;
    window.open(url, "_blank");
    navigator.clipboard.writeText(data.whatsapp_message || "").catch(() => {});
    toast.success("Mensaje copiado al portapapeles. WhatsApp abierto.");
  };

  return (
    <div className="mt-2 bg-green-50 border border-green-200 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-green-700 font-medium text-xs">📱 Mensaje listo para WhatsApp</span>
        {data.recipient_name && (
          <span className="text-xs text-green-600">→ {data.recipient_name}</span>
        )}
      </div>
      <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-white rounded-lg p-2 border border-green-100 max-h-32 overflow-y-auto font-sans">
        {data.whatsapp_message}
      </pre>
      <button
        onClick={handleOpen}
        className="mt-2 flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
      >
        <ExternalLink size={12} /> Abrir en WhatsApp
      </button>
    </div>
  );
}

function SaleCreatedBadge({ sale }) {
  return (
    <div className="mt-2 bg-brand-50 border border-brand-200 rounded-xl p-3 flex items-center gap-3">
      <CheckCircle size={18} className="text-brand-600 flex-shrink-0" />
      <div>
        <div className="text-sm font-semibold text-brand-800">
          {sale.type === "pedido" ? "Pedido" : sale.type === "cotizacion" ? "Cotización" : "Factura"} creado: {sale.folio}
        </div>
        <div className="text-xs text-brand-600">
          {sale.customer_name} · Total: {formatMXN(sale.total)}
        </div>
      </div>
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
        isUser ? "bg-brand-600" : "bg-slate-100"
      }`}>
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-brand-600" />}
      </div>
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        {/* Tool badges */}
        {msg.tools?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {msg.tools.map((t, i) => <ToolBadge key={i} tool={t} />)}
          </div>
        )}
        {/* Message bubble */}
        <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-brand-600 text-white rounded-tr-sm"
            : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm"
        }`}>
          {msg.content.split("\n").map((line, i) => (
            <span key={i}>{line}{i < msg.content.split("\n").length - 1 && <br />}</span>
          ))}
        </div>
        {/* WhatsApp button */}
        {msg.whatsapp_data && <WhatsAppButton data={msg.whatsapp_data} />}
        {/* Sale created badge */}
        {msg.sale_created && <SaleCreatedBadge sale={msg.sale_created} />}
        <span className="text-xs text-slate-400 px-1">
          {new Date(msg.ts || Date.now()).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </div>
  );
}

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load history on first open
  useEffect(() => {
    if (open && !historyLoaded) {
      api.get(`/ai/history?session_id=${SESSION_ID}`).then((r) => {
        const msgs = r.data.map((h) => ({
          role: h.role, content: h.content,
          ts: h.created_at, tools: [], whatsapp_data: null, sale_created: null
        }));
        setMessages(msgs);
        setHistoryLoaded(true);
      }).catch(() => setHistoryLoaded(true));
    }
  }, [open, historyLoaded]);

  // Scroll to bottom
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;
    setInput("");

    const userMsg = { role: "user", content: userText, ts: new Date().toISOString(), tools: [] };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    // Placeholder "thinking" message
    const thinkingId = Date.now();
    setMessages((prev) => [...prev, { role: "assistant", content: "...", ts: new Date().toISOString(), tools: [], _thinking: true, _id: thinkingId }]);

    try {
      const res = await api.post("/ai/chat", { message: userText, session_id: SESSION_ID });
      const d = res.data;
      const tools = (d.tool_results || []).map((t) => t.tool);
      setMessages((prev) => prev
        .filter((m) => m._id !== thinkingId)
        .concat({
          role: "assistant",
          content: d.response || "Sin respuesta.",
          ts: new Date().toISOString(),
          tools,
          whatsapp_data: d.whatsapp_data,
          sale_created: d.sale_created,
        })
      );
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m._id !== thinkingId).concat({
        role: "assistant",
        content: "Lo siento, ocurrió un error. Por favor intenta de nuevo.",
        ts: new Date().toISOString(), tools: []
      }));
      toast.error("Error al contactar al asistente IA");
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("¿Borrar el historial del chat?")) return;
    await api.delete(`/ai/history?session_id=${SESSION_ID}`).catch(() => {});
    setMessages([]);
    toast.success("Historial borrado");
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
        title="Asistente IA"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-7rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-brand-600 px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Bot size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm">RasputinERP AI</div>
              <div className="text-brand-200 text-xs">Asistente inteligente · GPT-4o</div>
            </div>
            <div className="flex gap-1">
              <button onClick={clearHistory} title="Borrar historial"
                className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <Trash2 size={14} />
              </button>
              <button onClick={() => setOpen(false)}
                className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {isEmpty && (
              <div className="space-y-4">
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot size={14} className="text-brand-600" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-slate-800 shadow-sm max-w-[85%]">
                    ¡Hola! Soy tu asistente IA. Puedo crear pedidos, consultar precios, revisar inventario y preparar mensajes de WhatsApp. ¿En qué te ayudo?
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-400 px-9">Sugerencias:</p>
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)}
                      className="w-full text-left text-xs text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 px-3 py-2 rounded-xl transition-colors ml-9 mr-2" style={{ maxWidth: "calc(100% - 2.5rem)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              m._thinking ? (
                <div key={i} className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot size={14} className="text-brand-600" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
                    <Loader2 size={16} className="text-brand-500 animate-spin" />
                  </div>
                </div>
              ) : <Message key={i} msg={m} />
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-100 bg-white flex-shrink-0">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Escribe una petición... (Enter para enviar)"
                rows={1}
                className="flex-1 resize-none border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 max-h-24 overflow-y-auto"
                style={{ minHeight: "38px" }}
                disabled={loading}
              />
              <button
                onClick={() => send()}
                disabled={!input.trim() || loading}
                className="w-9 h-9 flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5 text-center">
              Powered by GPT-4o · RasputinERP AI
            </p>
          </div>
        </div>
      )}
    </>
  );
}
